// The ⋯ menu: every button a page offers, in one place (samksly's row kebab, lifted to the page).
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { useAuth } from '../../stores/auth';
import { useCsvExport, type CsvExport } from './ExportCsvButton';

export interface MenuItem {
  label: ReactNode;
  /** lucide icon name. */
  icon?: string;
  onClick?: () => void;
  /** Internal route. */
  to?: string;
  /** External link, opened in a new tab. */
  href?: string;
  /** Download every row as filtered now; shown only with `<page>.export`. */
  export?: CsvExport;
  /** Hide unless the user holds this permission. */
  can?: string;
  disabled?: boolean;
  danger?: boolean;
  /** Hover text, usually why it is disabled. */
  title?: string;
  /** A rule above this item. */
  separator?: boolean;
}
/** Items may be written as `cond && {...}` so pages can spread conditions without ceremony. */
export type MenuEntry = MenuItem | false | null | undefined | 0 | '';

const ICONS = Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>;

export function useVisibleMenu(entries: MenuEntry[] | undefined): MenuItem[] {
  const can = useAuth((s) => s.can);
  return (entries ?? []).filter((e): e is MenuItem => !!e && (!e.can || can(e.can)) && (!e.export || can(`${e.export.page}.export`)));
}

function Row({ item, close }: { item: MenuItem; close: () => void }) {
  const exp = useCsvExport(item.export ?? { path: '', page: '' });
  const busy = !!item.export && exp.busy;
  const I = item.icon ? ICONS[item.icon] : item.export ? Icons.Download : null;
  const cls = `flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] transition ${item.disabled ? 'cursor-not-allowed text-ink-300' : item.danger ? 'text-red-600 hover:bg-red-50' : 'text-ink-800 hover:bg-ink-50'}`;
  const icon = busy ? <Icons.Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-ink-400" /> : I ? <I className={`h-3.5 w-3.5 shrink-0 ${item.danger && !item.disabled ? 'text-red-500' : 'text-ink-400'}`} /> : <span className="h-3.5 w-3.5 shrink-0" />;
  const body = <>{icon}<span className="min-w-0 flex-1 truncate">{item.label}</span>{item.export && exp.note && <span className="text-[10.5px] text-ink-400">{exp.note}</span>}</>;
  const sep = item.separator ? <div className="my-1 border-t border-ink-100" /> : null;
  if (item.to && !item.disabled) return <>{sep}<Link to={item.to} role="menuitem" title={item.title} className={cls} onClick={close}>{body}</Link></>;
  if (item.href && !item.disabled) return <>{sep}<a href={item.href} target="_blank" rel="noreferrer" role="menuitem" title={item.title} className={cls} onClick={close}>{body}</a></>;
  return (
    <>
      {sep}
      <button type="button" role="menuitem" disabled={item.disabled || busy} title={item.title} className={cls} onClick={() => {
        if (item.export) { void exp.run(); return; }
        item.onClick?.(); close();
      }}>{body}</button>
    </>
  );
}

/** One action shown in the open, as a pill: the same item a ⋯ menu would hold. */
function InlineAction({ item }: { item: MenuItem }) {
  const exp = useCsvExport(item.export ?? { path: '', page: '' });
  const busy = !!item.export && exp.busy;
  const I = item.icon ? ICONS[item.icon] : item.export ? Icons.Download : null;
  const cls = `inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-[12.5px] font-medium transition ${item.disabled ? 'cursor-not-allowed border-ink-200 bg-ink-50 text-ink-400' : item.danger ? 'border-red-600 bg-red-600 text-white hover:border-red-700 hover:bg-red-700' : item.export ? 'border-ink-200 bg-ink-100 text-ink-800 hover:bg-ink-200' : 'border-bronze-500 bg-bronze-500 text-white hover:border-bronze-600 hover:bg-bronze-600'}`;
  const body = <>{busy ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : I ? <I className="h-3.5 w-3.5" /> : null}<span className="hidden sm:inline">{item.label}</span></>;
  if (item.to && !item.disabled) return <Link to={item.to} title={item.title} className={cls}>{body}</Link>;
  if (item.href && !item.disabled) return <a href={item.href} target="_blank" rel="noreferrer" title={item.title} className={cls}>{body}</a>;
  return <button type="button" disabled={item.disabled || busy} title={item.title ?? (typeof item.label === 'string' ? item.label : undefined)} className={cls} onClick={() => { if (item.export) { void exp.run(); return; } item.onClick?.(); }}>{body}</button>;
}

/**
 * A page's actions: up to two are shown in the open; three or more collapse into the ⋯ menu.
 */
export function PageActions({ items }: { items: MenuEntry[] | undefined }) {
  const visible = useVisibleMenu(items);
  if (!visible.length) return null;
  if (visible.length <= 2) return <div className="flex shrink-0 items-center gap-1.5">{visible.map((it, i) => <InlineAction key={i} item={it} />)}</div>;
  return <ActionMenu items={items} />;
}

/**
 * A dropdown panel anchored to a button. The panel is portalled to <body> and fixed to the viewport, so a scrolling
 * table wrapper or an overflow-hidden card cannot clip it. It hangs under the button, flips above when there is no
 * room below, and is pulled inside the viewport edges on a phone. `prefer: 'up'` turns that round — above the button
 * (a drop-up, for a button at the bottom of a drawer), under it only when there is no room above. (Offsets, not a transform: the open animation owns
 * `transform` and would override it.) Closes on an outside click, Escape, scroll or resize.
 * Render the panel with `style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}`.
 */
export function useAnchoredPanel(open: boolean, setOpen: (v: boolean) => void, align: 'left' | 'right' = 'right', prefer: 'down' | 'up' = 'down') {
  const wrap = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !wrap.current || !panel.current) { setPos(null); return; }
    const b = wrap.current.getBoundingClientRect();
    const p = panel.current.getBoundingClientRect();
    const margin = 12, gap = 8;
    let left = align === 'right' ? b.right - p.width : b.left;
    left = Math.max(margin, Math.min(left, window.innerWidth - margin - p.width));
    const below = b.bottom + gap, above = b.top - gap - p.height;
    const fitsBelow = below + p.height <= window.innerHeight - margin, fitsAbove = above >= margin;
    const top = prefer === 'up' ? (fitsAbove || !fitsBelow ? Math.max(margin, above) : below) : (!fitsBelow && fitsAbove ? above : below);
    setPos({ top, left });
  }, [open, align, prefer]);
  useEffect(() => {
    if (!open) return;
    const inside = (t: EventTarget | null) => (wrap.current?.contains(t as Node) || panel.current?.contains(t as Node)) ?? false;
    const onDoc = (e: MouseEvent) => { if (!inside(e.target)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // A fixed panel would drift away from its button if the page scrolled under it: close instead.
    const onScroll = (e: Event) => { if (!panel.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll);
    };
  }, [open, setOpen]);
  return { wrap, panel, pos };
}

/** What a custom trigger gets: whether the menu is open, and the toggle (ActionsButton in ui/index — the labelled button). */
export interface MenuTriggerProps { open: boolean; toggle: () => void }

/**
 * Round ⋯ button with a dropdown. Permission-gated items drop out; the button disappears when nothing is left.
 * `align` says which edge of the button the panel hangs from; `up` opens it above the button (a drop-up); `trigger`
 * renders a button of the caller's own in place of the round one (the drawers' labelled "Actions" — 3 Nov 2026); that
 * panel takes the width of its longest item (up to the screen) so "Mark referencing passed (override)" reads in full.
 */
export function ActionMenu({ items, align = 'right', className = '', label = 'Actions', vertical = false, up = false, trigger }: {
  items: MenuEntry[] | undefined; align?: 'left' | 'right'; className?: string; label?: string; /** ⋮ instead of ⋯, a smaller button for a table row. */ vertical?: boolean;
  /** Prefer opening above the button. */ up?: boolean; trigger?: (p: MenuTriggerProps) => ReactNode;
}) {
  const visible = useVisibleMenu(items);
  const [open, setOpen] = useState(false);
  const { wrap, panel, pos } = useAnchoredPanel(open, setOpen, align, up ? 'up' : 'down');
  if (!visible.length) return null;
  return (
    <div ref={wrap} className={`relative ${className}`}>
      {trigger ? trigger({ open, toggle: () => setOpen((v) => !v) }) : <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open} aria-label={label} title={label}
        className={vertical
          ? `grid h-7 w-7 place-items-center rounded-md transition ${open ? 'bg-ink-100 text-ink-900' : 'text-ink-400 hover:bg-ink-100 hover:text-ink-900'}`
          : `grid h-8 w-8 place-items-center rounded-full border transition ${open ? 'border-ink-300 bg-ink-100 text-ink-900' : 'border-ink-200 bg-white text-ink-500 hover:bg-ink-100 hover:text-ink-900'}`}>
        {vertical ? <Icons.EllipsisVertical className="h-4 w-4" /> : <Icons.Ellipsis className="h-4 w-4" />}
      </button>}
      {open && createPortal(
        <div ref={panel} role="menu" style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999 }} className={`menu-pop z-50 ${trigger ? 'w-max min-w-60 max-w-[calc(100vw-24px)] sm:max-w-sm' : 'w-60'} overflow-hidden rounded-lg border border-ink-200 bg-white py-1 shadow-float`}>
          {visible.map((it, i) => <Row key={i} item={it} close={() => setOpen(false)} />)}
        </div>,
        document.body,
      )}
    </div>
  );
}
